import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useHelp } from './HelpContext';
import { loadHelpForPath } from './loadHelpArticle';
import HelpMarkdown from './components/HelpMarkdown';

/**
 * Right-side help slide-out (~50% width). Article updates when the route changes while open.
 */
export default function HelpDrawer() {
  const { isOpen, closeHelp } = useHelp();
  const location = useLocation();

  const article = useMemo(
    () => loadHelpForPath(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeHelp();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeHelp]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-labelledby="help-drawer-title">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 border-0 cursor-default"
        aria-label="Close help"
        onClick={closeHelp}
      />

      <aside
        className="absolute inset-y-0 right-0 w-full sm:w-1/2 bg-white shadow-2xl flex flex-col animate-[helpSlideIn_180ms_ease-out]"
      >
        <style>{`
          @keyframes helpSlideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>

        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-slate-50">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-blue-600">Page Help</p>
            <h2 id="help-drawer-title" className="text-lg font-semibold text-gray-900 truncate">
              {article.meta.title}
            </h2>
            {article.meta.summary ? (
              <p className="text-sm text-gray-600 mt-1">{article.meta.summary}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={closeHelp}
            className="shrink-0 rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close help panel"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <HelpMarkdown>{article.body}</HelpMarkdown>
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 bg-slate-50">
          <p className="text-xs text-gray-500">Content updates when you change pages while Help is open.</p>
        </footer>
      </aside>
    </div>
  );
}
